import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getApiUrl } from '../config/runtimeConfig';

import { getAccessToken, clearAuthStorage } from '../services/api';
const API_BASE = getApiUrl();

export default function DepositConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | completed | pending | failed | unknown | refunded
  const [amount, setAmount] = useState(null);
  const token = getAccessToken();

  const txRef = searchParams.get('tx_ref');
  const providerTransactionId = searchParams.get('transaction_id');

  useEffect(() => {
    if (!txRef) {
      setStatus('unknown');
      return;
    }
    persistProviderReference().finally(() => checkStatus());
  }, []);

  async function persistProviderReference() {
    if (!providerTransactionId) return;

    try {
      await fetch(`${API_BASE}/api/wallet/deposit/provider-reference`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reference: txRef,
          providerReference: providerTransactionId,
        }),
      });
    } catch (_) {
      // Identity persistence is best-effort here; the server-side webhook
      // and reconciliation paths remain authoritative and unchanged.
    }
  }

  async function checkStatus(attemptsLeft = 8) {
    try {
      const res = await fetch(`${API_BASE}/api/wallet/deposit/status/${txRef}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let data = null;
      try { data = await res.json(); } catch (_) {}

      if (!res.ok || !data?.transaction) {
        setStatus('unknown');
        if (attemptsLeft > 0) setTimeout(() => checkStatus(attemptsLeft - 1), 2000);
        return;
      }

      setAmount(data.transaction.amount);
      const localStatus = String(data.transaction.local_status || data.transaction.status || '').toLowerCase();
      const providerStatus = String(data.transaction.provider_status || '').toLowerCase();
      const refundStatus = String(data.transaction.refund_status || '').toLowerCase();

      if (localStatus === 'refunded' || refundStatus === 'refunded') {
        setStatus('refunded');
      } else if (localStatus === 'completed' || localStatus === 'successful') {
        setStatus('completed');
      } else if (localStatus === 'failed') {
        setStatus('failed');
      } else if (localStatus === 'pending' || providerStatus === 'pending' || providerStatus === 'processing') {
        setStatus('pending');
        if (attemptsLeft > 0) setTimeout(() => checkStatus(attemptsLeft - 1), 2000);
      } else {
        setStatus('unknown');
        if (attemptsLeft > 0) setTimeout(() => checkStatus(attemptsLeft - 1), 2000);
      }
    } catch (err) {
      setStatus('unknown');
      if (attemptsLeft > 0) setTimeout(() => checkStatus(attemptsLeft - 1), 2000);
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: '420px', margin: '0 auto', textAlign: 'center', paddingTop: '60px' }}>

      {status === 'checking' && (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <h2 style={{ color: '#fff' }}>Confirming your deposit...</h2>
          <p style={{ color: '#999' }}>This usually takes a few seconds</p>
        </>
      )}

      {status === 'completed' && (
        <>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
          <h2 style={{ color: '#00c896' }}>Deposit Successful!</h2>
          <p style={{ color: '#fff', fontSize: '20px', margin: '12px 0' }}>
            ₦{amount?.toLocaleString()} has been added to your wallet
          </p>
          <p style={{ color: '#999', marginBottom: '24px' }}>You're ready to play!</p>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%', padding: '14px', background: '#00c896',
              border: 'none', borderRadius: '10px', color: '#000',
              fontWeight: 'bold', fontSize: '16px'
            }}
          >
            Go to Wallet
          </button>
        </>
      )}

      {status === 'pending' && (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⌛</div>
          <h2 style={{ color: '#ffaa00' }}>Payment Processing</h2>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            Your payment is still being confirmed. This can take a few minutes. 
            Your wallet will update automatically once confirmed.
          </p>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%', padding: '14px', background: '#333',
              border: 'none', borderRadius: '10px', color: '#fff',
              fontWeight: 'bold', fontSize: '16px'
            }}
          >
            Back to Wallet
          </button>
        </>
      )}

      {status === 'unknown' && (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔎</div>
          <h2 style={{ color: '#ffaa00' }}>Payment Verification Pending</h2>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            We could not currently obtain authoritative payment status. No failure is being assumed.
            Please keep this reference for reconciliation.
          </p>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%', padding: '14px', background: '#333',
              border: 'none', borderRadius: '10px', color: '#fff',
              fontWeight: 'bold', fontSize: '16px'
            }}
          >
            Back to Wallet
          </button>
        </>
      )}

      {status === 'refunded' && (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>↩️</div>
          <h2 style={{ color: '#ffaa00' }}>Payment Refunded</h2>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            Flutterwave has confirmed that this payment was refunded.
          </p>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%', padding: '14px', background: '#333',
              border: 'none', borderRadius: '10px', color: '#fff',
              fontWeight: 'bold', fontSize: '16px'
            }}
          >
            Back to Wallet
          </button>
        </>
      )}

      {status === 'failed' && (
        <>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
          <h2 style={{ color: '#ff5555' }}>Deposit Not Confirmed</h2>
          <p style={{ color: '#999', marginBottom: '24px' }}>
            We couldn't confirm this payment. If money was deducted from your account, 
            please <Link to={`/support?ref=${txRef}&type=deposit`} style={{ color: '#00d4aa', textDecoration: 'underline' }}>contact support</Link> with your reference before trying again.
          </p>
          <button
            onClick={() => navigate('/wallet')}
            style={{
              width: '100%', padding: '14px', background: '#333',
              border: 'none', borderRadius: '10px', color: '#fff',
              fontWeight: 'bold', fontSize: '16px'
            }}
          >
            Back to Wallet
          </button>
        </>
      )}

    </div>
  );
}
